
import React, { useState, useRef } from 'react'
import {Upload, X, FileText, AlertTriangle, CheckCircle, Download} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface BulkUploadInsumosProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface InsumoRow {
  nome?: string
  tipo?: 'material' | 'mao_obra' | 'equipamento'
  unidadeMedida?: string
  precoUnitario?: number
  controlaEstoque?: boolean
  quantidadeEstoque?: number
  estoqueMinimo?: number
  observacoes?: string
}

interface ValidationError {
  row: number
  field: string
  message: string
}

const BulkUploadInsumos: React.FC<BulkUploadInsumosProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<InsumoRow[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadStep, setUploadStep] = useState<'upload' | 'preview' | 'processing' | 'complete'>('upload')
  const [processedCount, setProcessedCount] = useState(0)
  const [existingInsumos, setExistingInsumos] = useState<any[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = () => {
    setUploadedFile(null)
    setParsedData([])
    setValidationErrors([])
    setIsProcessing(false)
    setUploadStep('upload')
    setProcessedCount(0)
    setDragActive(false)
    setExistingInsumos([])
  }

  const handleClose = () => {
    if (!isProcessing) {
      resetState()
      onClose()
    }
  }

  // 🔥 SOLUÇÃO DEFINITIVA: Template CSV com BOM UTF-8 e encoding correto
  const downloadTemplate = () => {
    try {
      // Dados com caracteres especiais brasileiros
      const templateData = [
        ['nome', 'tipo', 'unidadeMedida', 'precoUnitario', 'controlaEstoque', 'quantidadeEstoque', 'estoqueMinimo', 'observacoes'],
        ['Cimento Portland CP-II-E-32', 'material', 'sc', '45,50', 'VERDADEIRO', '100', '10', 'Cimento para construção civil'],
        ['Areia Média Lavada', 'material', 'm³', '35,00', 'VERDADEIRO', '50', '5', 'Areia para argamassa e concreto'],
        ['Pedreiro Especializado', 'mao_obra', 'h', '25,00', 'FALSO', '', '', 'Mão de obra especializada em alvenaria'],
        ['Betoneira 400L Elétrica', 'equipamento', 'un', '2500,00', 'VERDADEIRO', '2', '1', 'Equipamento para mistura de concreto'],
        ['Tijolo Cerâmico 6 Furos', 'material', 'milheiro', '450,00', 'VERDADEIRO', '20', '5', 'Tijolo para vedação'],
        ['Soldador Certificado', 'mao_obra', 'h', '35,00', 'FALSO', '', '', 'Profissional com certificação em solda'],
        ['Aço CA-50 Ø 10mm', 'material', 'kg', '8,90', 'VERDADEIRO', '500', '50', 'Vergalhão para estruturas'],
        ['Tinta Látex Branca Premium', 'material', 'galão', '85,00', 'VERDADEIRO', '30', '5', 'Tinta acrílica de alta qualidade'],
        ['Cimento CP-V ARI Plus', 'material', 'sc', '52,30', 'VERDADEIRO', '80', '15', 'Cimento de alta resistência inicial'],
        ['Encanador Especializado', 'mao_obra', 'h', '28,00', 'FALSO', '', '', 'Profissional em instalações hidráulicas']
      ]

      // 🔥 CRÍTICO: Geração CSV com encoding UTF-8 forçado
      const csvLines = templateData.map(row => 
        row.map(cell => {
          const cellStr = String(cell)
          // Escapar aspas duplas e envolver campos com vírgula/aspas em aspas
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') || cellStr.includes('\r')) {
            return `"${cellStr.replace(/"/g, '""')}"`
          }
          return cellStr
        }).join(',')
      ).join('\r\n')

      // 🔥 SOLUÇÃO: BOM UTF-8 + encoding explícito
      const BOM = '\uFEFF'
      const csvContent = BOM + csvLines

      // 🔥 FORÇAR: Tipo MIME específico para CSV UTF-8
      const blob = new Blob([csvContent], { 
        type: 'text/csv;charset=utf-8' 
      })
      
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'template_insumos_utf8.csv'
      link.style.display = 'none'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast.success('Template CSV UTF-8 baixado com caracteres especiais corretos!')
      
    } catch (error) {
      console.error('Erro ao gerar template:', error)
      toast.error('Erro ao gerar template CSV')
    }
  }

  // 🔥 PARSER UTF-8 ROBUSTO: Forçar leitura em UTF-8
  const parseCSVWithUTF8 = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      try {
        // 🔥 CRÍTICO: Usar FileReader com encoding UTF-8 explícito
        const reader = new FileReader()
        
        reader.onload = (e) => {
          try {
            let text = e.target?.result as string
            
            if (!text) {
              reject(new Error('Arquivo vazio'))
              return
            }

            // 🔥 DETECTAR E REMOVER BOM se presente
            if (text.charCodeAt(0) === 0xFEFF) {
              text = text.slice(1)
            }

            // 🔥 NORMALIZAR quebras de linha
            text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
            
            const lines = text.split('\n').filter(line => line.trim() !== '')
            
            if (lines.length < 2) {
              reject(new Error('Arquivo deve ter pelo menos cabeçalho e uma linha de dados'))
              return
            }

            // 🔥 DETECTAR separador automaticamente
            const firstLine = lines[0]
            let separator = ','
            
            // Contar separadores para detectar o correto
            const commaCount = (firstLine.match(/,/g) || []).length
            const semicolonCount = (firstLine.match(/;/g) || []).length
            const tabCount = (firstLine.match(/\t/g) || []).length
            
            if (semicolonCount > commaCount && semicolonCount > tabCount) {
              separator = ';'
            } else if (tabCount > commaCount && tabCount > semicolonCount) {
              separator = '\t'
            }

            console.log('🔍 Separador detectado:', separator === ',' ? 'vírgula' : separator === ';' ? 'ponto-vírgula' : 'tab')

            // 🔥 PARSER CSV ROBUSTO que preserva caracteres UTF-8
            const parseCSVLine = (line: string): string[] => {
              const result: string[] = []
              let current = ''
              let inQuotes = false
              let i = 0

              while (i < line.length) {
                const char = line[i]
                
                if (char === '"') {
                  if (inQuotes && line[i + 1] === '"') {
                    // Aspas duplas escapadas
                    current += '"'
                    i += 2
                  } else {
                    // Alternar estado de aspas
                    inQuotes = !inQuotes
                    i++
                  }
                } else if (char === separator && !inQuotes) {
                  // Fim do campo
                  result.push(current.trim())
                  current = ''
                  i++
                } else {
                  // Caractere normal (incluindo UTF-8)
                  current += char
                  i++
                }
              }
              
              // Adicionar último campo
              result.push(current.trim())
              return result
            }

            // Processar cabeçalho
            const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, '').trim())
            console.log('📋 Cabeçalhos detectados:', headers)

            // Processar dados
            const rows: any[] = []
            for (let i = 1; i < lines.length; i++) {
              const line = lines[i].trim()
              if (!line) continue
              
              const values = parseCSVLine(line)
              const row: any = {}
              
              headers.forEach((header, index) => {
                let value = values[index] || ''
                // 🔥 PRESERVAR caracteres UTF-8 sem modificação
                row[header] = value
              })
              
              // Só adicionar se tiver nome
              if (row.nome && String(row.nome).trim() !== '') {
                rows.push(row)
              }
            }

            console.log('✅ Dados processados com UTF-8:', rows.length, 'linhas')
            console.log('🔤 Teste caracteres especiais:', rows[0]?.nome)
            
            resolve(rows)

          } catch (parseError) {
            console.error('❌ Erro no parser CSV:', parseError)
            reject(new Error('Erro ao processar CSV. Verifique o formato do arquivo.'))
          }
        }

        reader.onerror = () => {
          reject(new Error('Erro ao ler arquivo'))
        }

        // 🔥 FORÇAR: Leitura como texto UTF-8
        reader.readAsText(file, 'UTF-8')
        
      } catch (error) {
        console.error('❌ Erro na configuração do FileReader:', error)
        reject(error)
      }
    })
  }

  // Validar dados
  const validateRow = (row: any, index: number): ValidationError[] => {
    const errors: ValidationError[] = []
    const rowNum = index + 2

    // 🔥 VALIDAÇÃO: Nome sem restrições de caracteres especiais
    if (!row.nome || String(row.nome).trim().length < 2) {
      errors.push({ row: rowNum, field: 'nome', message: 'Nome é obrigatório e deve ter pelo menos 2 caracteres' })
    }

    // Validar tipo
    if (row.tipo && !['material', 'mao_obra', 'equipamento'].includes(row.tipo)) {
      errors.push({ row: rowNum, field: 'tipo', message: 'Tipo deve ser: material, mao_obra ou equipamento' })
    }

    // Validar preço
    if (row.precoUnitario && row.precoUnitario !== '') {
      let precoStr = String(row.precoUnitario).trim()
      
      // Remover símbolos de moeda
      precoStr = precoStr.replace(/[R$\s]/g, '')
      
      // Normalizar separadores decimais (aceitar vírgula e ponto)
      if (precoStr.includes(',') && precoStr.includes('.')) {
        // Formato brasileiro: 1.234,56
        precoStr = precoStr.replace(/\./g, '').replace(',', '.')
      } else if (precoStr.includes(',') && !precoStr.includes('.')) {
        // Apenas vírgula: 1234,56
        precoStr = precoStr.replace(',', '.')
      }
      
      const preco = parseFloat(precoStr)
      if (isNaN(preco) || preco < 0) {
        errors.push({ row: rowNum, field: 'precoUnitario', message: 'Preço deve ser um número válido maior ou igual a zero' })
      }
    }

    // Validar controle de estoque
    const controlaEstoque = parseBoolean(row.controlaEstoque)
    
    if (controlaEstoque) {
      if (row.quantidadeEstoque && row.quantidadeEstoque !== '') {
        const qtd = parseFloat(String(row.quantidadeEstoque).replace(',', '.'))
        if (isNaN(qtd) || qtd < 0) {
          errors.push({ row: rowNum, field: 'quantidadeEstoque', message: 'Quantidade deve ser um número válido >= 0' })
        }
      }

      if (row.estoqueMinimo && row.estoqueMinimo !== '') {
        const min = parseFloat(String(row.estoqueMinimo).replace(',', '.'))
        if (isNaN(min) || min < 0) {
          errors.push({ row: rowNum, field: 'estoqueMinimo', message: 'Estoque mínimo deve ser um número válido >= 0' })
        }
      }
    }

    return errors
  }

  // Converter string para boolean
  const parseBoolean = (value: any): boolean => {
    if (!value || value === '') return false
    const str = String(value).toLowerCase().trim()
    return str === 'verdadeiro' || str === 'true' || str === '1' || str === 'sim' || str === 'yes'
  }

  // Verificar se é arquivo Excel
  const isExcelFile = (file: File): boolean => {
    const name = file.name.toLowerCase()
    const type = file.type.toLowerCase()
    
    return name.endsWith('.xlsx') || 
           name.endsWith('.xls') || 
           type.includes('spreadsheet') ||
           type.includes('excel') ||
           type.includes('ms-excel') ||
           type.includes('officedocument')
  }

  // 🔥 ORIENTAÇÃO: Excel para CSV UTF-8
  const handleExcelFile = () => {
    toast.error('Para garantir caracteres especiais corretos, converta seu Excel para CSV UTF-8:\n1. Abra o arquivo no Excel\n2. Arquivo > Salvar Como\n3. Escolha "CSV UTF-8 (separado por vírgulas)"', {
      duration: 8000
    })
  }

  // Verificar se insumo já existe
  const isInsumoExistente = (nome: string): boolean => {
    if (!nome || nome.trim() === '') return false
    
    const normalizeString = (str: string) => {
      return str.toLowerCase()
                .trim()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
    }
    
    const nomeNormalizado = normalizeString(nome)
    
    return existingInsumos.some(insumo => 
      insumo.nome && normalizeString(insumo.nome) === nomeNormalizado
    )
  }

  // Processar arquivo
  const processFile = async (file: File) => {
    setIsProcessing(true)
    
    try {
      console.log('🔄 Iniciando processamento do arquivo:', file.name)
      
      // Carregar insumos existentes
      try {
        const existingData = await lumi.entities.insumos.list()
        setExistingInsumos(existingData?.list || [])
      } catch (error) {
        console.warn('⚠️ Erro ao carregar insumos existentes:', error)
        setExistingInsumos([])
      }

      // 🔥 VERIFICAR: Tipo de arquivo
      if (isExcelFile(file)) {
        console.log('❌ Arquivo Excel detectado - orientando para CSV UTF-8')
        handleExcelFile()
        setIsProcessing(false)
        return
      }

      // 🔥 PROCESSAR: Apenas CSV com UTF-8
      console.log('✅ Processando arquivo CSV com UTF-8...')
      const rawData = await parseCSVWithUTF8(file)
      
      console.log('📊 Dados extraídos:', rawData.length, 'registros')
      
      if (rawData.length === 0) {
        toast.error('Arquivo vazio ou sem dados válidos. Use o template CSV fornecido.')
        setIsProcessing(false)
        return
      }

      // Validar e processar dados
      const errors: ValidationError[] = []
      const validData: InsumoRow[] = []
      const processedNames = new Set<string>()

      rawData.forEach((row, index) => {
        const rowErrors = validateRow(row, index)
        errors.push(...rowErrors)

        if (rowErrors.length === 0 && row.nome && String(row.nome).trim() !== '') {
          const nomeTrimmed = String(row.nome).trim()
          
          const normalizeString = (str: string) => {
            return str.toLowerCase()
                      .trim()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
          }
          
          const nomeNormalizado = normalizeString(nomeTrimmed)
          
          if (processedNames.has(nomeNormalizado)) {
            console.log(`📝 Insumo duplicado no arquivo ignorado: ${nomeTrimmed}`)
            return
          }

          if (isInsumoExistente(nomeTrimmed)) {
            console.log(`🔄 Insumo já existe no sistema, ignorado: ${nomeTrimmed}`)
            return
          }

          processedNames.add(nomeNormalizado)
          
          const controlaEstoque = parseBoolean(row.controlaEstoque)
          
          // Processar preço
          let precoProcessado = 0
          if (row.precoUnitario && row.precoUnitario !== '') {
            let precoStr = String(row.precoUnitario).trim()
            precoStr = precoStr.replace(/[R$\s]/g, '')
            
            if (precoStr.includes(',') && precoStr.includes('.')) {
              precoStr = precoStr.replace(/\./g, '').replace(',', '.')
            } else if (precoStr.includes(',')) {
              precoStr = precoStr.replace(',', '.')
            }
            
            precoProcessado = parseFloat(precoStr) || 0
          }
          
          validData.push({
            nome: nomeTrimmed, // 🔥 PRESERVAR caracteres UTF-8 originais
            tipo: row.tipo || 'material',
            unidadeMedida: String(row.unidadeMedida || 'un').trim(),
            precoUnitario: precoProcessado,
            controlaEstoque,
            quantidadeEstoque: controlaEstoque && row.quantidadeEstoque ? parseFloat(String(row.quantidadeEstoque).replace(',', '.')) : 0,
            estoqueMinimo: controlaEstoque && row.estoqueMinimo ? parseFloat(String(row.estoqueMinimo).replace(',', '.')) : 0,
            observacoes: String(row.observacoes || '').trim()
          })
        }
      })

      console.log('✅ Dados válidos processados:', validData.length)
      console.log('🔤 Teste final caracteres especiais:', validData[0]?.nome)
      
      setValidationErrors(errors)
      setParsedData(validData)
      setUploadStep('preview')
      
    } catch (error) {
      console.error('❌ Erro ao processar arquivo:', error)
      toast.error(`Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Formato não suportado'}`)
    } finally {
      setIsProcessing(false)
    }
  }

  // Processar dados válidos
  const processValidData = async () => {
    if (parsedData.length === 0) {
      toast.error('Nenhum dado válido para processar')
      return
    }

    setUploadStep('processing')
    setIsProcessing(true)
    setProcessedCount(0)

    try {
      let successCount = 0
      let errorCount = 0

      for (let i = 0; i < parsedData.length; i++) {
        try {
          const insumoData = {
            ...parsedData[i],
            ativo: true,
            creator: 'admin',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }

          console.log('💾 Salvando insumo:', insumoData.nome)
          await lumi.entities.insumos.create(insumoData)
          successCount++
        } catch (error) {
          console.error(`❌ Erro ao criar insumo ${parsedData[i].nome}:`, error)
          errorCount++
        }
        
        setProcessedCount(i + 1)
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      if (successCount > 0) {
        toast.success(`${successCount} insumos importados com sucesso!`)
        if (errorCount > 0) {
          toast.error(`${errorCount} insumos falharam na importação`)
        }
        setUploadStep('complete')
        setTimeout(() => {
          onSuccess()
          resetState()
          onClose()
        }, 2000)
      } else {
        toast.error('Nenhum insumo foi importado')
        setUploadStep('preview')
      }

    } catch (error) {
      console.error('❌ Erro no processamento em massa:', error)
      toast.error('Erro no processamento dos dados')
      setUploadStep('preview')
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    const file = files[0]
    if (!file) return

    const fileName = file.name.toLowerCase()
    const fileType = file.type.toLowerCase()
    
    const isCSV = fileName.endsWith('.csv') || fileType.includes('csv')
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || 
                   fileType.includes('spreadsheet') || fileType.includes('excel')
    
    if (!isCSV && !isExcel) {
      toast.error('Apenas arquivos CSV ou Excel são aceitos')
      return
    }

    console.log('📁 Arquivo aceito:', file.name, 'Tipo:', file.type)
    setUploadedFile(file)
    await processFile(file)
  }

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'material': return 'Material'
      case 'mao_obra': return 'Mão de Obra'
      case 'equipamento': return 'Equipamento'
      default: return tipo
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Upload em Massa - Insumos</h2>
          </div>
          {!isProcessing && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {uploadStep === 'upload' && (
            <div className="space-y-6">
              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 mb-2">🔥 Template CSV UTF-8 - Caracteres Especiais Garantidos</h3>
                    <p className="text-blue-700 text-sm mb-3">
                      <strong>✅ Solução definitiva para acentos e caracteres especiais!</strong>
                      <br />
                      Este template gera CSV com encoding UTF-8 nativo. Colunas: <strong>nome, tipo, unidadeMedida, precoUnitario, controlaEstoque, quantidadeEstoque, estoqueMinimo, observacoes</strong>.
                      <br />
                      <em>🎯 Use <strong>VERDADEIRO/FALSO</strong> para controlaEstoque. Aceita vírgula ou ponto para decimais.</em>
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Baixar Template CSV UTF-8 (Caracteres Corretos)
                    </button>
                  </div>
                </div>
              </div>

              {/* Instrução Excel */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-900 mb-2">📋 Se você tem arquivo Excel</h3>
                    <p className="text-amber-700 text-sm">
                      Para converter Excel para CSV UTF-8:
                      <br />
                      <strong>1.</strong> Abra seu arquivo no Excel
                      <br />
                      <strong>2.</strong> Arquivo → Salvar Como
                      <br />
                      <strong>3.</strong> Escolha: <strong>"CSV UTF-8 (separado por vírgulas)"</strong>
                      <br />
                      <strong>4.</strong> Faça upload do arquivo .csv gerado
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Arraste seu arquivo CSV UTF-8 aqui ou clique para selecionar
                </h3>
                <p className="text-gray-500 mb-4">
                  <strong>Recomendado:</strong> Use o template CSV UTF-8 fornecido
                  <br />
                  <span className="text-green-600 font-medium">✅ Garantia de caracteres especiais corretos</span>
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,.xlsx,.xls"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Selecionar Arquivo CSV/Excel
                </button>
              </div>

              {isProcessing && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Processando arquivo com UTF-8...</p>
                </div>
              )}
            </div>
          )}

          {uploadStep === 'preview' && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Resumo do Arquivo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Arquivo:</span>
                    <span className="ml-2 font-medium">{uploadedFile?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Registros válidos:</span>
                    <span className="ml-2 font-medium text-green-600">{parsedData.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Erros encontrados:</span>
                    <span className="ml-2 font-medium text-red-600">{validationErrors.length}</span>
                  </div>
                </div>
                {parsedData.length > 0 && (
                  <div className="mt-3 text-sm">
                    <span className="text-green-600 font-medium">✅ Caracteres especiais processados com UTF-8</span>
                    <br />
                    <span className="text-gray-600">Exemplo: {parsedData[0]?.nome}</span>
                  </div>
                )}
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-red-900 mb-2">Erros de Validação</h3>
                      <div className="max-h-40 overflow-y-auto">
                        {validationErrors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            Linha {error.row}, campo "{error.field}": {error.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Data */}
              {parsedData.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">
                    Preview dos Dados Válidos ({parsedData.length} registros)
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estoque</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {parsedData.slice(0, 10).map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900 whitespace-normal break-words">
                                <strong>{item.nome}</strong>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {getTipoLabel(item.tipo || 'material')}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.unidadeMedida}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                R$ {(item.precoUnitario || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.controlaEstoque ? `${item.quantidadeEstoque || 0} (min: ${item.estoqueMinimo || 0})` : 'Não controlado'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {parsedData.length > 10 && (
                      <div className="bg-gray-50 px-4 py-2 text-sm text-gray-600 text-center">
                        ... e mais {parsedData.length - 10} registros
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-between">
                <button
                  onClick={() => setUploadStep('upload')}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Voltar
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  {parsedData.length > 0 && (
                    <button
                      onClick={processValidData}
                      disabled={isProcessing}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isProcessing ? 'Processando...' : `Importar ${parsedData.length} Insumos`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {uploadStep === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importando Insumos com UTF-8</h3>
              <p className="text-gray-600 mb-4">
                Processando {processedCount} de {parsedData.length} registros...
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(processedCount / parsedData.length) * 100}%` }}
                ></div>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {Math.round((processedCount / parsedData.length) * 100)}% concluído
              </div>
            </div>
          )}

          {uploadStep === 'complete' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importação Concluída!</h3>
              <p className="text-gray-600">
                {parsedData.length} insumos foram importados com caracteres especiais corretos.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BulkUploadInsumos
